import { useCallback, useEffect, useMemo, useState } from 'react';
import { useRouter } from 'next/router';
import { useDispatch, useSelector } from 'react-redux';
import PropTypes from 'prop-types';
import { useAnalytics } from 'use-analytics';
import { useCookies } from 'react-cookie';
import { useAuth0 } from '@auth0/auth0-react';
import { BiLock } from 'react-icons/bi';
import {
  checkIsLoadedIn,
  getTravelAgencySettingsRequest,
  checkSitePermission,
  verifyPagePasswordSuccess,
} from '../store/slices/user';
import {
  areCookiesEnabled,
  ERROR_CODES,
  logErrorToConsole,
  PageLinks,
  UserRolePermissions,
} from '../constants';
import { setTripSearchProperty } from '../store/slices/tripSearch';
import PasswordScreen from '../components/PasswordScreen';
import { Button } from '../components';
import ModalSpinner from '../components/ModalSpinner';
import Loader from '../components/Loader';
import CookieDisabledModal from '../components/CookieDisabledModal';
import getJwtExpiry from '../utils/JWT/getJwtExpiry';

/*
 doContinueWithoutLogin: true when you want to continue without login.
 Use ProtectRoute with doContinueWithoutLogin to make sure user logged in or not before sending
 any request to backend.
 For ex, in checkout page if user logged in we want to include authorization header in trip request.
 checkout page is accessible without login. if we don't use ProtectRoute it does not include
 authorization header in request even when user logged in
 */

const ProtectRoute = ({ children, doContinueWithoutLogin, doPasswordProtectPage }) => {
  const [doShowPasswordScreen, setDoShowPasswordScreen] = useState(undefined);
  const router = useRouter();
  const dispatch = useDispatch();
  const {
    travelAgencyAliasName,
    selectedCurrency: selectedCurrencyQuery,
    tripquote,
    brochure,
    dedicatedHotel,
  } = router.query;
  const [cookies] = useCookies();
  const { isLoggedIn, loaded } = useSelector(checkIsLoadedIn);
  const { track, identify } = useAnalytics();
  const { email, travelAgency, isPagePasswordVerified, travelAgencyError } = useSelector(
    (store) => store.user,
  );
  const { currencyCode } = useSelector((store) => store.tripSearch);
  const { user } = useAuth0();
  const canUnverifiedUserAccessSite = useSelector(
    checkSitePermission(UserRolePermissions.canUnverifiedUserAccessSite),
  );
  const { isSelfBookingAllowed } = useSelector((store) => store.trip);
  const tripConfirmation = useSelector((store) => store.trip.tripConfirmation);
  const isSelfBookingEnabled = tripConfirmation && tripConfirmation.doAllowSelfBookingTripQuote;

  const isDedicatedHotelPage = router.route === PageLinks.DedicatedHotelPage.route;
  const doPasswordProtectDedicatedHotelPage =
    travelAgency?.settings?.doPasswordProtectHotelDedicatedPage;

  const doShowPasswordScreenConfig =
    doContinueWithoutLogin &&
    doPasswordProtectPage &&
    travelAgency &&
    travelAgency.doPasswordProtectClientFacingPage &&
    (isDedicatedHotelPage ? doPasswordProtectDedicatedHotelPage : true);

  const [doShowModal, setDoShowModal] = useState(false);
  const [showCookieModal, setShowCookieModal] = useState(false);

  const loadRateCodes = useCallback(() => {
    if (
      router.pathname === PageLinks.Home.url ||
      router.pathname === PageLinks.Trips.url ||
      router.query.pageName === PageLinks.AddCar.pageName ||
      router.query.pageName === PageLinks.ChangeCar.pageName ||
      router.query.pageName === PageLinks.AddHotel.pageName ||
      router.query.pageName === PageLinks.ChangeHotel.pageName ||
      router.query.pageName === PageLinks.AddFlight.pageName ||
      router.query.pageName === PageLinks.ChangeFlight.pageName ||
      (router.pathname && router.pathname.indexOf(PageLinks.Compare.url) > -1) ||
      (router.pathname && router.pathname.indexOf(PageLinks.Brochure.url) > -1)
    ) {
      // dispatch(getHotelRateCodesRequest());
      // dispatch(getFlightRateCodesRequest());
      // dispatch(getRentalCarDiscountCodesRequest());
    }
  }, [router]);

  const checkPageAccessCookie = useMemo(() => {
    if (cookies.twpac) {
      // ASSUMED COOKIE EXPIRES SAME TIME AS JWT TOKEN EXPIRY
      const tokenExpiry = getJwtExpiry(cookies.twpac);
      if (tokenExpiry) {
        const eightHoursFromNow = new Date(Date.now() + 8 * 60 * 60 * 1000);
        if (tokenExpiry < eightHoursFromNow) {
          return false;
        }
      }
      return true;
    }
    return false;
  }, [cookies.twpac]);

  const getErrorMessage = (error) => {
    if (!error) return null;
    if (typeof error === 'string') return error;
    if (typeof error === 'object') return error.message || 'Something went wrong';
    return 'Something went wrong';
  };

  const redirectToAgencyPage = useCallback(() => {
    if (travelAgencyError?.redirectUrl) {
      router.push(travelAgencyError.redirectUrl);
      track('redirectToAgencyPage', { travelAgencyId: travelAgency?.id });
    }
  }, [router, track, travelAgency, travelAgencyError]);

  useEffect(() => {
    if (!areCookiesEnabled()) {
      logErrorToConsole('❌ Cookies are disabled'); // Handle disabled cookies: prompt user to enable, fallback to localStorage/sessionStorage, block access, or something.
      track('cookiesDisabled', { email: user?.email, travelAgencyId: travelAgency?.id });
      setShowCookieModal(true);
      return;
    }
    if (
      loaded &&
      isLoggedIn &&
      travelAgency &&
      !canUnverifiedUserAccessSite &&
      user &&
      !user.email_verified
    ) {
      if (router.pathname.indexOf('emailVerification') === -1) {
        track('redirectToVerifyEmailPage', {
          email: user && user.email,
          travelAgencyId: travelAgency && travelAgency.id,
        });
        if (!travelAgency || !travelAgency.id) {
          track('redirectToVerifyEmailPage_without_travelagency', { email: user && user.email });
        }
        router.push('/emailVerification');
      }
      dispatch(getTravelAgencySettingsRequest({}));
    } else if (loaded && !isLoggedIn && !doContinueWithoutLogin) {
      if (!travelAgencyError) {
        track('redirectToLogin', { travelAgencyId: travelAgency && travelAgency.id });
        if (!travelAgency || !travelAgency.id) {
          track('redirectToLogin_without_travelagency');
        }
        // Replace spaces to underscore because of auth0 issue
        const decodedRouterPath = decodeURIComponent(router.asPath).replaceAll(' ', '_');
        router.push(`/login?returnUrl=${encodeURIComponent(decodedRouterPath)}`);
      }
    } else if (loaded && isLoggedIn) {
      dispatch(getTravelAgencySettingsRequest({}));
      // dispatch(getQuickLinksRequest());
      loadRateCodes();
    } else if (loaded) {
      dispatch(
        getTravelAgencySettingsRequest({
          travelAgencyAliasName: !isLoggedIn ? travelAgencyAliasName : undefined,
        }),
      );
      if (!doShowPasswordScreen) {
        loadRateCodes();
      } else if (doShowPasswordScreen && checkPageAccessCookie) {
        dispatch(verifyPagePasswordSuccess({ response: true }));
      }
    }
    // don't add travelagency as dependency, otherwise it will send endless request
    // to travelAgency when user not login
    // oxlint-disable-next-line react/exhaustive-deps
  }, [
    dispatch,
    doContinueWithoutLogin,
    isLoggedIn,
    loaded,
    router,
    track,
    travelAgencyAliasName,
    loadRateCodes,
    canUnverifiedUserAccessSite,
    user,
  ]);

  useEffect(() => {
    // Pass email as a trait as well as the id, so PostHog stores it as a person
    // property and feature flags can target on it (e.g. internal users only).
    if (email) identify(email, { email });
  }, [email, identify]);

  useEffect(() => {
    if (selectedCurrencyQuery !== undefined) {
      if (selectedCurrencyQuery !== currencyCode) {
        dispatch(setTripSearchProperty({ currencyCode: selectedCurrencyQuery }));
      }
      return;
    }
    if (!travelAgency || !travelAgency.doSupportCurrency) return;
    const cookieVal = cookies[`selectedCurrency:${travelAgency.id}`];
    if (cookieVal !== undefined) {
      if (cookieVal !== currencyCode) {
        dispatch(setTripSearchProperty({ currencyCode: cookieVal }));
      }
      return;
    }
    const agencyDefaultCurrency = travelAgency.settings?.defaultCurrency ?? '';
    if (agencyDefaultCurrency !== currencyCode) {
      dispatch(setTripSearchProperty({ currencyCode: agencyDefaultCurrency }));
    }
  }, [cookies, currencyCode, dispatch, selectedCurrencyQuery, travelAgency]);

  useEffect(() => {
    if (
      isSelfBookingAllowed ||
      isSelfBookingEnabled ||
      tripquote !== undefined ||
      brochure !== undefined ||
      dedicatedHotel !== undefined
    ) {
      /// Booking trip quote - don't prompt password when allowed to self book
      setDoShowPasswordScreen(false);
      return;
    }
    if (!isLoggedIn && doShowPasswordScreenConfig) {
      setDoShowPasswordScreen(true);
    } else {
      setDoShowPasswordScreen(false);
    }
  }, [
    doShowPasswordScreenConfig,
    isLoggedIn,
    isSelfBookingAllowed,
    isSelfBookingEnabled,
    tripquote,
    brochure,
    dedicatedHotel,
  ]);

  useEffect(() => {
    if (isPagePasswordVerified) {
      loadRateCodes();
      setDoShowPasswordScreen(false);
    }
  }, [isPagePasswordVerified, loadRateCodes]);

  useEffect(() => {
    if (travelAgencyError) {
      setDoShowModal(true);
    }
  }, [travelAgencyError]);

  if (showCookieModal) {
    return <CookieDisabledModal isOpen={showCookieModal} />;
  }

  // TODO loader component is state of null, add in the future
  if (!loaded) {
    return <Loader />;
  }
  // Block rendering until setDoShowPasswordScreen is called
  // To avoid calling hooks in the children component
  if (doShowPasswordScreen === undefined) {
    return <Loader />;
  }

  const handleRefresh = () => {
    router.reload();
    track('onClickRefreshPage');
  };

  let view = <></>;
  if (
    (doContinueWithoutLogin && !doShowPasswordScreen) ||
    isLoggedIn ||
    (doShowPasswordScreen && checkPageAccessCookie)
  ) {
    view = children;
  } else if (doShowPasswordScreen) {
    view = (
      <PasswordScreen
        setDoShowPasswordScreen={setDoShowPasswordScreen}
        doShowPasswordScreen={doShowPasswordScreen}
      />
    );
  }

  const RefreshComponent = () => (
    <div className="flex h-1/2 w-full items-center justify-center">
      <div className="flex flex-col items-start px-4 py-2">
        <h1 className="text-2xl">Something went wrong</h1>
        <div className="text-sm mt-3">
          {travelAgencyError && <div className="mb-1">{getErrorMessage(travelAgencyError)}</div>}
          Please try refreshing the page. If you still see this message, our service might be
          disrupted. Contact support for further assistance.
        </div>
        <Button onClick={handleRefresh} size={Button.buttonSizes.SM} className="mt-3">
          <span>Refresh</span>
        </Button>
      </div>
    </div>
  );

  const RedirectComponent = () => (
    <div className="flex h-1/2 w-full items-center justify-center">
      <div className="flex flex-col items-start px-4 py-2">
        <h1 className="text-2xl">
          <BiLock className="inline-block text-3xl mb-2 mr-2" />
          You&apos;re on the wrong agency site
        </h1>
        <div className="text-sm mt-3">
          You&apos;re trying to access a travel agency site that isn&apos;t linked to your account.
          Please go back to your agency&apos; page to continue booking.
          <div className="mt-3">
            <b>Need help?</b> Contact your agency administrator or support team.
          </div>
        </div>
        {travelAgencyError?.redirectUrl ? (
          <Button onClick={redirectToAgencyPage} size={Button.buttonSizes.SM} className="mt-4">
            Go to My Agency Page
          </Button>
        ) : (
          <></>
        )}
      </div>
    </div>
  );

  return (
    <>
      {doShowModal && (
        <ModalSpinner isOpen doHidelogo size="xs">
          {travelAgencyError?.code === ERROR_CODES.AGENCY_MISMATCH ? (
            <RedirectComponent />
          ) : (
            <RefreshComponent />
          )}
        </ModalSpinner>
      )}
      {view}
    </>
  );
};

ProtectRoute.propTypes = {
  children: PropTypes.oneOfType([
    PropTypes.objectOf(PropTypes.any),
    PropTypes.arrayOf(PropTypes.any),
  ]).isRequired,
  doContinueWithoutLogin: PropTypes.bool,
  doPasswordProtectPage: PropTypes.bool,
};

ProtectRoute.defaultProps = {
  doContinueWithoutLogin: false,
  doPasswordProtectPage: false,
};

export default ProtectRoute;
